<template>
  <div class="q-pa-md">
    <div class="container">
      <!-- ── Page header ─────────────────────────────── -->
      <div class="page-header q-mb-lg">
        <h2 class="text-h4 text-weight-bold text-grey-9 q-mb-xs">
          Resultado da Pesquisa
        </h2>
        <p class="text-body1 text-grey-6">
          <template v-if="loader">A carregar...</template>
          <template v-else-if="totalResults > 0">
            {{ totalResults }} imóve{{
              totalResults === 1 ? "l encontrado" : "is encontrados"
            }}
          </template>
          <template v-else>Nenhum imóvel encontrado</template>
        </p>
      </div>

      <!-- ── Active filter chips ─────────────────────── -->
      <div
        v-if="activeFilters.length"
        class="q-mb-md row items-center q-gutter-sm"
      >
        <span class="text-caption text-grey-5 text-weight-medium q-mr-xs"
          >Filtros activos:</span
        >
        <q-chip
          v-for="f in activeFilters"
          :key="f.key"
          removable
          dense
          color="primary"
          text-color="white"
          size="sm"
          @remove="removeFilter(f.key)"
        >
          {{ f.label }}: {{ f.value }}
        </q-chip>
        <q-btn
          flat
          dense
          no-caps
          size="sm"
          color="grey-6"
          label="Limpar tudo"
          icon="close"
          @click="clearAllFilters"
        />
      </div>

      <!-- ── Refine filter bar ───────────────────────── -->
      <q-card class="refine-card q-mb-xl" flat bordered>
        <q-card-section class="q-py-sm">
          <div class="row q-col-gutter-sm items-end">
            <div class="col-12 col-sm-6 col-md-3">
              <q-input
                v-model="refineForm.location"
                outlined
                dense
                clearable
                label="Localização"
              >
                <template v-slot:prepend>
                  <q-icon name="place" color="primary" />
                </template>
              </q-input>
            </div>

            <div class="col-6 col-sm-4 col-md-2">
              <q-select
                v-model="refineForm.bedroom_amount"
                :options="minRoomOptions"
                option-label="label"
                option-value="value"
                emit-value
                map-options
                outlined
                dense
                clearable
                label="Quartos (mín.)"
                options-dense
              >
                <template v-slot:prepend>
                  <q-icon name="bed" color="primary" />
                </template>
              </q-select>
            </div>

            <div class="col-6 col-sm-4 col-md-2">
              <q-select
                v-model="refineForm.bathroom_amount"
                :options="minRoomOptions"
                option-label="label"
                option-value="value"
                emit-value
                map-options
                outlined
                dense
                clearable
                label="Casas de banho (mín.)"
                options-dense
              >
                <template v-slot:prepend>
                  <q-icon name="bathtub" color="primary" />
                </template>
              </q-select>
            </div>

            <div class="col-6 col-sm-4 col-md-2">
              <q-select
                v-model="refineForm.garage_space"
                :options="minGarageOptions"
                option-label="label"
                option-value="value"
                emit-value
                map-options
                outlined
                dense
                clearable
                label="Garagem (mín.)"
                options-dense
              >
                <template v-slot:prepend>
                  <q-icon name="garage" color="primary" />
                </template>
              </q-select>
            </div>

            <div class="col-6 col-md-3">
              <q-btn
                unelevated
                no-caps
                color="primary"
                class="full-width refine-btn"
                @click="applyRefine"
              >
                <q-icon name="search" class="q-mr-sm" />
                Refinar
              </q-btn>
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- ── Results ────────────────────────────────── -->
      <q-infinite-scroll @load="onLoad" :offset="250">
        <!-- Skeleton -->
        <div v-if="loader" class="row q-col-gutter-lg">
          <div v-for="n in 8" :key="'sk-' + n" class="col-12 col-sm-6 col-md-3">
            <q-card flat bordered class="imovel-card">
              <q-skeleton height="180px" square />
              <q-card-section>
                <q-skeleton type="text" width="40%" class="q-mb-xs" />
                <q-skeleton type="text" width="80%" class="q-mb-xs" />
                <q-skeleton type="text" width="60%" class="q-mb-md" />
                <div class="row q-gutter-md q-mb-md">
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="40px" />
                  <q-skeleton type="text" width="30px" />
                </div>
                <q-separator class="q-my-md" />
                <div class="row items-center justify-between">
                  <q-skeleton type="text" width="50%" />
                  <q-skeleton type="QBtn" />
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!imoveis.length"
          class="empty-state text-center q-py-xl"
        >
          <q-icon name="search_off" size="72px" color="grey-4" />
          <h3 class="text-h5 text-grey-6 q-mt-md q-mb-sm">
            Nenhum imóvel encontrado
          </h3>
          <p class="text-body1 text-grey-5 q-mb-lg">
            Tente ajustar os filtros para ampliar a pesquisa.
          </p>
          <q-btn
            unelevated
            no-caps
            color="primary"
            label="Limpar filtros"
            @click="clearAllFilters"
          />
        </div>

        <!-- Cards grid -->
        <div v-else class="row q-col-gutter-lg">
          <div
            v-for="(imovel, index) in imoveis"
            :key="index"
            class="col-12 col-sm-6 col-md-3"
          >
            <q-card class="imovel-card" flat bordered>
              <div class="badge-container">
                <q-badge
                  :color="imovel.objective === 'Venda' ? 'green-7' : 'blue-7'"
                  floating
                  class="tipo-negocio-badge"
                >
                  {{ imovel.objective }}
                </q-badge>
              </div>

              <div class="image-container">
                <q-img
                  :src="imovel.primary_image_url"
                  :ratio="16 / 10"
                  class="cursor-pointer"
                  @click="verDetalhes(imovel)"
                >
                  <template v-slot:loading>
                    <q-skeleton class="fit" />
                  </template>
                </q-img>
              </div>

              <q-card-section>
                <div class="text-overline text-primary q-mb-xs">
                  {{ imovel.property_type }}
                </div>
                <div class="text-h6 text-weight-bold q-mb-sm ellipsis-2-lines">
                  {{ imovel.title }}
                </div>
                <div class="row items-center q-mb-md text-grey-7">
                  <q-icon name="location_on" size="18px" class="q-mr-xs" />
                  <span class="text-body2">{{ imovel.location }}</span>
                </div>
                <div class="row items-center q-gutter-md q-mb-md">
                  <div class="row items-center">
                    <q-icon name="bed" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.bedroom_amount
                    }}</span>
                  </div>
                  <div class="row items-center">
                    <q-icon name="bathtub" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.bathroom_amount
                    }}</span>
                  </div>
                  <div class="row items-center">
                    <q-icon name="square_foot" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium"
                      >{{ imovel.area }}m²</span
                    >
                  </div>
                  <div class="row items-center">
                    <q-icon name="garage" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.garage_spaces
                    }}</span>
                  </div>
                </div>
                <q-separator class="q-my-md" />
                <div class="row items-center justify-between">
                  <div class="text-h5 text-weight-bold text-secondary">
                    {{ formatPrice(imovel.price, imovel.currency) }}
                  </div>
                  <q-btn
                    round
                    color="secondary"
                    icon="arrow_forward"
                    size="md"
                    @click="verDetalhes(imovel)"
                  />
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-infinite-scroll>

      <!-- ── Pagination ──────────────────────────────── -->
      <div v-if="!loader && imoveis.length" class="row justify-center q-mt-xl">
        <q-pagination
          v-model="currentPage"
          :max="totalPages"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
          @update:model-value="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "@/vuex/store";
import apiClient from "@/config/api";

const route = useRoute();
const router = useRouter();

// ── Options (for chip label resolution) ──────────────────
const options = ref({ objectives: [], property_types: [], conditions: [] });

async function fetchOptions() {
  try {
    const { data } = await apiClient.get("/properties/options");
    if (data.success) options.value = data.data;
  } catch {
    /* keep empty arrays */
  }
}

// ── Filter select options ─────────────────────────────────
const minRoomOptions = [1, 2, 3, 4, 5].map((n) => ({
  label: `${n}+`,
  value: n,
}));
const minGarageOptions = [1, 2, 3].map((n) => ({ label: `${n}+`, value: n }));

// ── Refine form state (pre-filled from URL) ───────────────
const refineForm = ref(buildForm(route.query));

function buildForm(q) {
  return {
    location: q.location || "",
    bedroom_amount: q.bedroom_amount ? Number(q.bedroom_amount) : null,
    bathroom_amount: q.bathroom_amount ? Number(q.bathroom_amount) : null,
    garage_space: q.garage_space ? Number(q.garage_space) : null,
  };
}

const applyRefine = () => {
  const query = { ...route.query };
  // update or remove each refinable param
  const set = (key, val) => {
    if (val != null && val !== "") query[key] = val;
    else delete query[key];
  };
  set("location", refineForm.value.location);
  set("bedroom_amount", refineForm.value.bedroom_amount);
  set("bathroom_amount", refineForm.value.bathroom_amount);
  set("garage_space", refineForm.value.garage_space);
  router.push({ name: "imoveis-search", query });
};

// ── Active filter chips ───────────────────────────────────
const activeFilters = computed(() => {
  const q = route.query;
  const chips = [];
  const find = (arr, id) => arr.find((o) => String(o.id) === String(id));

  if (q.objective) {
    const match = find(options.value.objectives, q.objective);
    chips.push({
      key: "objective",
      label: "Objetivo",
      value: match?.name ?? q.objective,
    });
  }
  if (q.property_type) {
    const match = find(options.value.property_types, q.property_type);
    chips.push({
      key: "property_type",
      label: "Tipo",
      value: match?.name ?? q.property_type,
    });
  }
  if (q.condition) {
    const match = find(options.value.conditions, q.condition);
    chips.push({
      key: "condition",
      label: "Condição",
      value: match?.name ?? q.condition,
    });
  }
  if (q.location)
    chips.push({ key: "location", label: "Localização", value: q.location });
  if (q.bedroom_amount)
    chips.push({
      key: "bedroom_amount",
      label: "Quartos",
      value: `${q.bedroom_amount}+`,
    });
  if (q.bathroom_amount)
    chips.push({
      key: "bathroom_amount",
      label: "Casas de banho",
      value: `${q.bathroom_amount}+`,
    });
  if (q.garage_space)
    chips.push({
      key: "garage_space",
      label: "Garagem",
      value: `${q.garage_space}+`,
    });
  return chips;
});

const removeFilter = (key) => {
  const query = { ...route.query };
  delete query[key];
  router.push({ name: "imoveis-search", query });
};

const clearAllFilters = () => {
  router.push({ name: "imoveis-search", query: {} });
};

// ── Results state ─────────────────────────────────────────
const currentPage = ref(1);
const totalPages = ref(1);
const totalResults = ref(0);
const loader = ref(false);
const imoveis = ref([]);

function getProperties(page = 1, query = route.query) {
  loader.value = true;
  const filters = {
    page,
    objective: query.objective,
    property_type: query.property_type,
    condition: query.condition,
    location: query.location,
    bedroom_amount: query.bedroom_amount,
    bathroom_amount: query.bathroom_amount,
    garage_space: query.garage_space,
  };

  store
    .dispatch("properties/getPropertiesMultipleFilters", filters)
    .then(() => {
      const d = store.state.properties.data;
      imoveis.value = d.data ?? [];
      totalPages.value = d.last_page ?? 1;
      totalResults.value = d.total ?? imoveis.value.length;
    })
    .finally(() => {
      loader.value = false;
    });
}

// Re-fetch and sync form whenever query params change
// watch(
//   () => route.query,
//   (newQuery) => {
//     currentPage.value = 1;
//     refineForm.value = buildForm(newQuery);
//     getProperties(1, newQuery);
//   },
//   { deep: true },
// );
watch(
  () => route.query,
  (newQuery, oldQuery) => {
    refineForm.value = buildForm(newQuery);

    const newFilters = { ...newQuery };
    delete newFilters.page;

    const oldFilters = { ...(oldQuery || {}) };
    delete oldFilters.page;

    const filtersChanged =
      JSON.stringify(newFilters) !== JSON.stringify(oldFilters);

    if (filtersChanged) {
      currentPage.value = 1;
      getProperties(1, newQuery);
    } else {
      currentPage.value = Number(newQuery.page) || 1;
      getProperties(currentPage.value, newQuery);
    }
  },
  { deep: true },
);

const formatPrice = (price, currency) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(price);

const verDetalhes = (imovel) =>
  router.push({ name: "imovel-details", params: { slug: imovel.slug } });

const onLoad = (_index, done) => setTimeout(done, 500);

const onPageChange = (page) => {
  router.replace({
    name: "imoveis-search",
    query: { ...route.query, page },
  });
};

onMounted(() => {
  fetchOptions();
  const page = Number(route.query.page) || 1;
  currentPage.value = page;
  getProperties(page, route.query);
});
</script>

<style scoped>
.container {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Refine bar ───────────────────────────── */
.refine-card {
  border-radius: 12px !important;
  border-color: #e5e7eb !important;
}

.refine-btn {
  height: 40px;
  font-weight: 600;
}

/* ── Empty state ──────────────────────────── */
.empty-state {
  padding: 4rem 0;
}

/* ── Property cards ───────────────────────── */
.imovel-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 16px;
  overflow: hidden;
}

.imovel-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.image-container {
  position: relative;
  overflow: hidden;
}

.badge-container {
  position: relative;
}

.tipo-negocio-badge {
  position: absolute;
  top: 225px;
  left: 120px;
  z-index: 1;
  padding: 6px 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.ellipsis-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 56px;
}

.q-img {
  transition: transform 0.3s ease;
}
.imovel-card:hover .q-img {
  transform: scale(1.05);
}
</style>
